import React, { useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock,
  Search,
  XCircle,
} from "lucide-react"

import { ComplianceFiltersPopover } from "@/components/compliance/filters/ComplianceFiltersPopover"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useComplianceFilters } from "@/contexts/ComplianceFiltersContext"
import {
  ComplianceRecord,
  EligibilityStatus,
  PersonCompliance,
  useComplianceRecords,
} from "@/hooks/useComplianceRecords"
import { supabase } from "@/lib/supabase"

import CompliancePersonDrawer from "./CompliancePersonDrawer"

const LicenceChecksTab: React.FC = () => {
  const {
    searchQuery,
    setSearchQuery,
    locationFilter,
    sheriffTypeFilter,
    driverManagerTeamFilter,
    dspFilter,
    eligibilityFilter,
    setEligibilityFilter,
  } = useComplianceFilters()
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)

  useEffect(() => {
    setCurrentPage(0)
  }, [
    searchQuery,
    locationFilter,
    sheriffTypeFilter,
    driverManagerTeamFilter,
    dspFilter,
    eligibilityFilter,
  ])

  const { data, isLoading, error } = useComplianceRecords({
    searchQuery,
    locationFilter: locationFilter !== "all" ? locationFilter : "",
    sheriffTypeFilter: sheriffTypeFilter !== "all" ? sheriffTypeFilter : "",
    driverManagerTeamFilter: driverManagerTeamFilter !== "all" ? driverManagerTeamFilter : "",
    dspFilter: dspFilter !== "all" ? dspFilter : "",
    eligibilityFilter,
    limit: pageSize,
    offset: currentPage * pageSize,
  })

  const records = data?.records || []
  const summary = data?.summary ?? { all: 0, eligible: 0, ineligible: 0, failed: 0, pending: 0 }
  const total = data?.total ?? 0

  const totalPages = Math.ceil(total / pageSize)
  const startRecord = currentPage * pageSize + 1
  const endRecord = Math.min((currentPage + 1) * pageSize, total)

  const handleEligibilityFilter = (filter: EligibilityStatus | "all") => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setEligibilityFilter(filter as any)
    setCurrentPage(0) // Reset to first page when filter changes
  }

  const handleFilterChange = () => {
    setCurrentPage(0) // Reset to first page when any filter changes
  }

  const badgeStyles: Record<
    EligibilityStatus,
    {
      label: string
      Icon: React.ComponentType<{ className?: string }>
      badgeClass: string
      iconClass: string
    }
  > = {
    Eligible: {
      label: "Eligible",
      Icon: CheckCircle2,
      badgeClass: "bg-green-50 text-green-700 border-green-200",
      iconClass: "bg-green-100 text-green-700",
    },
    Ineligible: {
      label: "Ineligible",
      Icon: XCircle,
      badgeClass: "bg-red-50 text-red-700 border-red-200",
      iconClass: "bg-red-100 text-red-700",
    },
    Failed: {
      label: "Failed",
      Icon: AlertTriangle,
      badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
      iconClass: "bg-amber-100 text-amber-700",
    },
    Pending: {
      label: "Pending",
      Icon: Clock,
      badgeClass:
        "bg-palette-jungleGreen000 text-palette-jungleGreen700 border-palette-jungleGreen200",
      iconClass: "bg-palette-jungleGreen100 text-palette-jungleGreen700",
    },
  }

  const badgeBaseClass =
    "inline-flex h-8 w-40 items-center justify-between gap-3 rounded-sm border font-medium text-xs cursor-default pointer-events-none px-0.5"

  const getEligibilityBadge = (eligibility: EligibilityStatus) => {
    const config = badgeStyles[eligibility]
    if (!config) {
      return (
        <Badge variant="secondary" className={`${badgeBaseClass} px-4 py-2 hover:bg-secondary`}>
          {eligibility}
        </Badge>
      )
    }

    const { Icon, label, badgeClass, iconClass } = config

    return (
      <Badge className={`${badgeBaseClass} ${badgeClass}`}>
        <span className="flex-1 px-2 text-left">{label}</span>
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-sm ${iconClass}`}>
          <Icon className="h-4 w-4" />
        </span>
      </Badge>
    )
  }

  const getPointsBadge = (points: number) => {
    if (points === 0) {
      return <span className="text-palette-jungleGreen500 text-sm">0</span>
    }
    const bgClass =
      points >= 6
        ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
        : "bg-green-100 text-green-800 hover:bg-green-100"
    return (
      <Badge
        className={`${bgClass} rounded-sm px-2.5 py-1 text-xs cursor-default pointer-events-none`}
      >
        {points}
      </Badge>
    )
  }

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-palette-jungleGreen900">Licence Checks</h1>
        <p className="text-sm text-palette-jungleGreen600 mt-1">
          View and manage DVLA licence checks for your workforce. Track driving points,
          endorsements, and eligibility status to ensure compliance with driving regulations and
          company policies.
        </p>
      </div>

      {/* Status Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          {
            value: "all" as const,
            label: "All",
            count: summary.all,
            Icon: Circle,
            activeClasses: "bg-blue-50 text-blue-700 border-blue-200",
            badgeClasses: "bg-blue-100 text-blue-700",
            iconColor: "text-blue-500",
            iconActive: "text-blue-600",
          },
          {
            value: "Eligible" as const,
            label: "Eligible",
            count: summary.eligible,
            Icon: CheckCircle2,
            activeClasses: "bg-green-50 text-green-700 border-green-200",
            badgeClasses: "bg-green-100 text-green-700",
            iconColor: "text-green-500",
            iconActive: "text-green-600",
          },
          {
            value: "Ineligible" as const,
            label: "Ineligible",
            count: summary.ineligible,
            Icon: XCircle,
            activeClasses: "bg-red-50 text-red-700 border-red-200",
            badgeClasses: "bg-red-100 text-red-700",
            iconColor: "text-red-500",
            iconActive: "text-red-600",
          },
          {
            value: "Failed" as const,
            label: "Failed",
            count: summary.failed,
            Icon: AlertTriangle,
            activeClasses: "bg-amber-50 text-amber-700 border-amber-200",
            badgeClasses: "bg-amber-100 text-amber-700",
            iconColor: "text-amber-500",
            iconActive: "text-amber-600",
          },
          {
            value: "Pending" as const,
            label: "Pending",
            count: summary.pending,
            Icon: Clock,
            activeClasses:
              "bg-palette-jungleGreen000 text-palette-jungleGreen700 border-palette-jungleGreen200",
            badgeClasses: "bg-palette-jungleGreen100 text-palette-jungleGreen700",
            iconColor: "text-palette-jungleGreen500",
            iconActive: "text-palette-jungleGreen600",
          },
        ].map((option) => {
          const isActive = eligibilityFilter === option.value
          return (
            <button
              key={option.value}
              onClick={() => handleEligibilityFilter(option.value)}
              className={`inline-flex items-center gap-3 rounded-sm border px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? option.activeClasses
                  : "bg-white text-palette-jungleGreen700 border-palette-jungleGreen200 hover:bg-palette-jungleGreen000"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <option.Icon
                  className={`h-4 w-4 ${isActive ? (option.iconActive ?? option.iconColor) : option.iconColor}`}
                />
                {option.label}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  isActive
                    ? option.badgeClasses
                    : "bg-palette-jungleGreen100 text-palette-jungleGreen600"
                }`}
              >
                {option.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-palette-jungleGreen500" />
          <Input
            placeholder="Search"
            className="pl-8"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setCurrentPage(0)
            }}
          />
        </div>

        <ComplianceFiltersPopover
          title="Filter Compliance Records"
          description="Refine your search with filters below"
          showEligibilityFilter={false}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* Pagination Info */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-palette-jungleGreen600">
          {total > 0 ? `${startRecord} - ${endRecord} of ${total}` : "0 results"}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value))
              setCurrentPage(0)
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-md flex-1 overflow-auto bg-white">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="h-10">
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600">
                Person
              </TableHead>
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600">
                Driver Status
              </TableHead>
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600">
                Depot
              </TableHead>
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600 whitespace-nowrap">
                Last Checked
              </TableHead>
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600">
                Points
              </TableHead>
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600">
                Endorsements
              </TableHead>
              <TableHead className="py-2 text-xs font-semibold text-palette-jungleGreen600">
                Eligibility
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="[&>td]:py-4">
                <TableCell colSpan={7} className="text-center">
                  <Spinner className="mx-auto" />
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow className="[&>td]:py-4">
                <TableCell colSpan={7} className="text-center text-palette-jungleGreen500">
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <ComplianceRow
                  key={record.id}
                  record={record}
                  getEligibilityBadge={getEligibilityBadge}
                  getPointsBadge={getPointsBadge}
                  onRowClick={() => {
                    setSelectedPersonId(record.id)
                  }}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading records:{" "}
            {error instanceof Error ? error.message : "An unknown error occurred"}
          </AlertDescription>
        </Alert>
      )}

      {selectedPersonId && (
        <CompliancePersonDrawerWrapper
          personId={selectedPersonId}
          open={!!selectedPersonId}
          onOpenChange={(open) => !open && setSelectedPersonId(null)}
        />
      )}
    </div>
  )
}

const ComplianceRow: React.FC<{
  record: ComplianceRecord
  onRowClick: () => void
  getEligibilityBadge: (eligibility: EligibilityStatus) => React.ReactNode
  getPointsBadge: (points: number) => React.ReactNode
}> = ({ record, onRowClick, getEligibilityBadge, getPointsBadge }) => {
  const primaryName = [record.first_name, record.surname].filter(Boolean).join(" ").trim()
  const secondaryId = record.person || "-"

  return (
    <TableRow
      className="cursor-pointer hover:bg-palette-jungleGreen000 text-sm [&>td]:py-2 [&>td]:align-middle"
      onClick={onRowClick}
    >
      <TableCell>
        <div className="flex flex-col leading-tight">
          <span className="font-medium text-sm">{primaryName || secondaryId}</span>
          <span className="text-[11px] text-palette-jungleGreen500">{secondaryId}</span>
        </div>
      </TableCell>
      <TableCell>{record.status || "-"}</TableCell>
      <TableCell>{record.depot || "-"}</TableCell>
      <TableCell>
        {record.check_date ? format(new Date(record.check_date), "dd/MM/yyyy") : "-"}
      </TableCell>
      <TableCell>{getPointsBadge(record.points)}</TableCell>
      <TableCell>
        {record.endorsements && record.endorsements.length > 0
          ? record.endorsements.join(", ")
          : "-"}
      </TableCell>
      <TableCell>{getEligibilityBadge(record.eligibility)}</TableCell>
    </TableRow>
  )
}

// Wrapper component to fetch full person data for drawer
const CompliancePersonDrawerWrapper: React.FC<{
  personId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}> = ({ personId, open, onOpenChange }) => {
  const { data: person, isLoading } = useQuery({
    queryKey: ["person-compliance", personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("people")
        .select(
          `
                    *,
                    driver_licence_records (
                        id,
                        driving_licence_number,
                        licence_status,
                        licence_type,
                        entitlement,
                        endorsements,
                        created_at
                    )
                `,
        )
        .eq("id", personId)
        .single()

      if (error) throw error

      // Sort licence records by date
      if (data.driver_licence_records) {
        data.driver_licence_records = data.driver_licence_records.sort(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (a: any, b: any) =>
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
        )
      }

      return data as unknown as PersonCompliance
    },
    enabled: open && !!personId,
  })

  // Don't render drawer until person data is loaded
  if (!person) {
    if (isLoading) {
      return null // Still loading, don't render yet
    }
    return null // No person data, don't render
  }

  return <CompliancePersonDrawer person={person} open={open} onOpenChange={onOpenChange} />
}

export default LicenceChecksTab
